import { sortByRecent } from "../../../helpers/sortByRecent.helper";

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export const normalizeStringList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

export const unwrapPermissionPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload || {};
  if (Array.isArray(payload)) return payload;
  if (payload.name || payload.permission || payload.permissionName || payload.code) {
    return payload;
  }
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return unwrapPermissionPayload(payload.data);
  }
  if (payload.result && typeof payload.result === "object" && !Array.isArray(payload.result)) {
    return unwrapPermissionPayload(payload.result);
  }
  return payload;
};

export const normalizePermission = (rawInput = {}) => {
  if (typeof rawInput === "string") {
    const name = rawInput.trim();
    return {
      id: name,
      permissionId: name,
      name,
      description: "",
      allow: [],
      deny: [],
    };
  }

  const raw = unwrapPermissionPayload(rawInput) || {};
  const name = firstDefined(
    raw.name,
    raw.permission,
    raw.permissionName,
    raw.code,
    raw.key,
    ""
  );
  const permissionId = firstDefined(raw.id, raw._id, raw.permissionId, name);

  return {
    ...raw,
    id: permissionId,
    permissionId,
    name,
    description: firstDefined(raw.description, raw.desc, ""),
    allow: normalizeStringList(raw.allow || raw.allows),
    deny: normalizeStringList(raw.deny || raw.denied || raw.denies || raw.denyList),
    createdAt: firstDefined(
      raw.createdAt,
      raw.created_at,
      raw.creationDate,
      raw.createdDate
    ),
    updatedAt: firstDefined(
      raw.updatedAt,
      raw.updated_at,
      raw.lastModifiedDate,
      raw.lastModified
    ),
  };
};

export const sortPermissions = (list = []) => sortByRecent(list);

export const mergePermissionDetail = (current, detail) => {
  const base = normalizePermission(current);
  if (!detail) return base;

  const raw = unwrapPermissionPayload(detail) || {};
  const next = normalizePermission({ ...base, ...raw });

  if (
    !("deny" in raw) &&
    !("denied" in raw) &&
    !("denies" in raw) &&
    !("denyList" in raw)
  ) {
    next.deny = base.deny;
  }
  if (!("allow" in raw) && !("allows" in raw)) {
    next.allow = base.allow;
  }

  return next;
};

export const normalizePermissionList = (payload) => {
  const source = unwrapPermissionPayload(payload);
  let list = [];
  if (Array.isArray(payload)) list = payload.map(normalizePermission);
  else if (Array.isArray(payload?.content)) {
    list = payload.content.map(normalizePermission);
  } else if (Array.isArray(payload?.data)) {
    list = payload.data.map(normalizePermission);
  } else if (Array.isArray(payload?.permissions)) {
    list = payload.permissions.map(normalizePermission);
  } else if (Array.isArray(source)) {
    list = source.map(normalizePermission);
  } else if (
    source &&
    (source.name || source.permission || source.permissionName || source.code)
  ) {
    list = [normalizePermission(source)];
  }

  return sortByRecent(list);
};

/** Same names shown on the Permissions page (GET /permission). */
export const toPermissionCatalogNames = (payload) => {
  const seen = new Set();
  return normalizePermissionList(payload)
    .map((item) => String(item.name || "").trim())
    .filter((name) => {
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
};

export const matchesPermissionSearch = (permission, filters = {}) => {
  const name = String(filters.name || "").trim().toLowerCase();
  if (!name) return true;
  return (
    String(permission.name || "").toLowerCase().includes(name) ||
    String(permission.description || "").toLowerCase().includes(name)
  );
};

export const collectPermissionSuggestions = (permissions = []) => {
  const set = new Set();
  permissions.forEach((item) => {
    [...(item.allow || []), ...(item.deny || [])].forEach((value) => {
      if (value) set.add(String(value));
    });
  });
  return Array.from(set).sort();
};
